import React, { useState, useEffect } from "react";
import axios from "axios";
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ChannelType, OrderStatus, OrderQueues, OrderFilters, PaymentStatus } from "helpers/enums";
import moment from "moment";
import { KeyValuePair } from "helpers";
import { Dictionary } from "lodash";

interface IOrdersContext {
    recordCount: number
    search: ISearch
    orders: IOrder[]
    isLoading: boolean
    anchorEl: Element | undefined
    selectedFilter: string
    orderQueuesCount: KeyValuePair<OrderQueues, number>[]
    filtersByQueue: KeyValuePair<string, string[]>[]

    updateSearch: (newSearch: ISearch) => void
    doSearch: (values: ISearch) => void
    searchOrders: (searchRequest: ISearch) => Promise<void>
    handleChangePage: (event: any, page: number) => void
    openEdit: (filterName: string, event: any) => void
    closeEdit: () => void
    changeQueue: (queue: OrderQueues | '') => void
}

export let ordersContext = React.createContext<IOrdersContext>({} as IOrdersContext);

let { Provider } = ordersContext;

interface IOrder {
    id: string
    orderId: string
    orderStatus: OrderStatus
    paymentStatus: PaymentStatus
    erpOrderNumber: string
    channelCode: string
    channelType: ChannelType
    createdOn: moment.Moment
    orderDate: moment.Moment
    currency: string

    amounts: IAmount
}
interface IAmount {
    subtotal: number
    shipping: number
    tax: number
    total: number
}

interface ISearch {
    queue: OrderQueues
    orderNumber: string
    pageNr: number
    pageSize: number
    status: string
    channel: string
    channelType: string
    dateFrom: moment.Moment | undefined | null
    dateTo: moment.Moment | undefined | null
    sku: string
}

export let OrdersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let location = useLocation();
    let navigate = useNavigate();


    const defaultSearch: ISearch = {
        queue: OrderQueues.Dashboard,
        orderNumber: '',
        pageNr: 0,
        pageSize: 20,
        status: '',
        channel: '',
        channelType: '',
        dateFrom: undefined,
        dateTo: undefined,
        sku: '',
    }

    const allFilters = [
        OrderFilters.OrderNumber,
        OrderFilters.Channel,
        OrderFilters.ChannelType,
        OrderFilters.Status,
        OrderFilters.DateFrom,
        OrderFilters.DateTo,
        OrderFilters.Sku
    ]

    const filtersByQueue = [
        new KeyValuePair<string, string[]>(OrderQueues.AllOrders, allFilters),
        new KeyValuePair<string, string[]>(OrderQueues.NewOrders, [
            OrderFilters.OrderNumber,
            OrderFilters.Channel,
            OrderFilters.ChannelType,
            OrderFilters.DateFrom,
            OrderFilters.DateTo,
            OrderFilters.Sku
        ]),
        new KeyValuePair<string, string[]>(OrderQueues.NotPaidOrders, [
            OrderFilters.OrderNumber,
            OrderFilters.Channel,
            OrderFilters.ChannelType,
            OrderFilters.DateFrom,
            OrderFilters.DateTo,
            OrderFilters.Sku
        ]),
        new KeyValuePair<string, string[]>(OrderQueues.ERPSyncErrors, [
            OrderFilters.OrderNumber,
            OrderFilters.Channel,
            OrderFilters.ChannelType,
            OrderFilters.DateFrom,
            OrderFilters.DateTo,
            OrderFilters.Sku
        ]),
        new KeyValuePair<string, string[]>(OrderQueues.SentToERP, [
            OrderFilters.OrderNumber,
            OrderFilters.Channel,
            OrderFilters.ChannelType,
            OrderFilters.DateFrom,
            OrderFilters.DateTo,
            OrderFilters.Sku
        ]),
        new KeyValuePair<string, string[]>(OrderQueues.PartiallyShipped, [
            OrderFilters.OrderNumber,
            OrderFilters.Channel,
            OrderFilters.ChannelType,
            OrderFilters.DateFrom,
            OrderFilters.DateTo,
            OrderFilters.Sku
        ]),
        new KeyValuePair<string, string[]>(OrderQueues.Closed, [
            OrderFilters.OrderNumber,
            OrderFilters.Channel,
            OrderFilters.ChannelType,
            OrderFilters.DateFrom,
            OrderFilters.DateTo,
            OrderFilters.Sku
        ]),
        new KeyValuePair<string, string[]>(OrderQueues.Cancelled, [
            OrderFilters.OrderNumber,
            OrderFilters.Channel,
            OrderFilters.ChannelType,
            OrderFilters.DateFrom,
            OrderFilters.DateTo,
            OrderFilters.Sku
        ]),
    ]

    let [isLoading, setLoading] = useState(true);

    let [orders, setOrders] = useState<IOrder[]>([]);
    let [recordCount, setTotalRecords] = useState(0);
    let [selectedFilter, setSelectedFilter] = useState<string>('');
    let [anchorEl, setAnchorEl] = useState<Element>();

    let [orderQueuesCount, setQueuesCount] = useState<KeyValuePair<OrderQueues, number>[]>([]);
    let [search, setSearch] = useState<ISearch>(defaultSearch);


    useEffect(() => {
        let getOrderQueuesCount = async () => {
            try {
                let res = await axios.get('/api/Order/GetOrderQueuesCount');
                setQueuesCount(res.data.items);
            } catch (err) { }
        }

        getOrderQueuesCount();

    }, [search.queue])

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Navigate to={location.pathname} state={{ search: { ...search, queue: OrderQueues.Dashboard } }} replace={true} />


    let processState = (state: any) => {
        let request = { ...search }

        if (state.search) {
            request = {
                ...request,
                ...state.search
            }
        }

        setSearch(request);

        if (request.queue !== OrderQueues.Dashboard)
            searchOrders(request);
        else
            setLoading(false);
    }


    let doSearch = (values: ISearch) => {
        let request = {
            ...values,
            dateFrom: values.dateFrom != null ? moment(values.dateFrom).toDate() : null,
            dateTo: values.dateTo != null ? moment(values.dateTo).toDate() : null,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        navigate(location.pathname, { state: { search: request } });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchOrders = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Order/SearchOrders', searchRequest);

            setOrders(response.data.items);
            setTotalRecords(response.data.recordCount);

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        navigate(location.pathname, { state: { search: request } });
    }


    let openEdit = (filterName: string, event: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedFilter(filterName);
    }

    let closeEdit = () => {
        setAnchorEl(undefined);
        setSelectedFilter('');
    }

    let changeQueue = (queue: OrderQueues | '') => {
        let request = {
            ...search,
            ...defaultSearch,
            queue
        } as ISearch

        navigate(location.pathname, { state: { search: request } })
    }


    return (
        <Provider value={{
            orderQueuesCount,
            recordCount,
            search,
            orders,
            isLoading,
            anchorEl,
            selectedFilter,
            filtersByQueue,

            updateSearch,
            doSearch,
            searchOrders,
            handleChangePage,
            openEdit,
            closeEdit,
            changeQueue
        }}>
            {children}
        </Provider>
    );
};